import ScalesIcon from "@redotech/redo-icons/scales-01.svg";
import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { produce } from "immer";
import * as React from "react";
import { Step, StepId, StepType, StepTypeDetailsProps } from "../step";

export interface TreatmentState {
  next: StepId | undefined;
  name: string;
  percentage: number;
}

export interface AbTestState {
  treatments: TreatmentState[];
  customTitle?: string;
  name: string;
}

export const AB_TEST = (
  Details: React.ComponentType<StepTypeDetailsProps<AbTestState>>,
): StepType<AbTestState, ModelStep.ABTest> => ({
  Details: (props: StepTypeDetailsProps<AbTestState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: "A/B test",
  valid(state) {
    return (
      !!state.treatments.length &&
      state.treatments.every((treatment) => treatment.name && treatment.next) &&
      state.treatments.every(
        (treatment) => treatment.percentage >= 0 && treatment.percentage <= 100,
      ) &&
      state.treatments.reduce(
        (acc, treatment) => acc + treatment.percentage,
        0,
      ) === 100
    );
  },
  Icon: ScalesIcon,
  description(state) {
    return "A/B test return flow";
  },
  downstream(state) {
    return state.treatments.map((treatment) => ({
      id: treatment.next,
      label: treatment.name,
    }));
  },
  maxDownstream(state) {
    return state.treatments.length;
  },
  empty: { treatments: [], name: "" },
  defaultInitialStateFn(steps: Map<StepId, Step>) {
    for (const step of steps.values()) {
      if (step.type.title === this.title) {
        const newState: AbTestState = { name: step.state.name, treatments: [] };
        for (const treatment of step.state.treatments) {
          newState.treatments.push({
            name: treatment.name,
            percentage: treatment.percentage,
            next: undefined,
          });
        }
        return newState;
      }
    }
    return this.empty;
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      for (const treatment of state.treatments) {
        if (treatment.next === stepId) {
          treatment.next = undefined;
        }
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    return produce(state, (draft) => {
      if (handleIndex >= 0 && handleIndex < draft.treatments.length) {
        const treatment = draft.treatments[handleIndex];
        if (treatment) {
          treatment.next = targetStepId;
        }
      } else {
        while (draft.treatments.length <= handleIndex) {
          draft.treatments.push({
            next: undefined,
            name: `Treatment ${draft.treatments.length + 1}`,
            percentage: 0,
          });
        }
        const treatment = draft.treatments[handleIndex];
        if (treatment) {
          treatment.next = targetStepId;
        }
      }
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      for (const treatment of draft.treatments) {
        if (treatment.next === targetStepId) {
          treatment.next = undefined;
        }
      }
    });
  },
  toModel(state, id): ModelStep.ABTest {
    return {
      customTitle: state.customTitle,
      type: ModelStep.AB_TEST,
      name: state.name,
      treatments: state.treatments.map((treatment) => ({
        name: treatment.name,
        percentage: treatment.percentage,
        next: id(treatment.next!),
      })),
    };
  },
  fromModel(model, id) {
    return {
      customTitle: model.customTitle,
      name: model.name,
      treatments: model.treatments.map((treatment) => ({
        name: treatment.name,
        percentage: treatment.percentage,
        next: id(treatment.next),
      })),
    };
  },
});

export function getReturnReasonStep(
  returnReasonTitle: string,
  Details: React.ComponentType<StepTypeDetailsProps<AbTestState>>,
): StepType<AbTestState, ModelStep.ABTest> {
  return {
    ...AB_TEST(Details),
    title: returnReasonTitle,
    toModel(state, id): ModelStep.ABTest {
      return { ...AB_TEST(Details).toModel(state, id) };
    },
  };
}
